/**
 * The hint ladder of a Learn problem (`LN-RDR01`, docs/35 §5 item 3,
 * `STYLE_GUIDE.md` §5).
 *
 * A problem is authored as one `.pc-exercise` card holding three callouts, and
 * the author writes them in the order the reader should meet them:
 *
 *   <div class="pc-exercise">
 *     <div class="pc-exercise-head">
 *       <span class="pc-exercise-num">Problem 7: char-code</span> …
 *     …the story, the input and output, the sample, the starter code…
 *     <div class="pc-callout" data-hint="1"><p class="pc-callout-title">Hint 1</p>…
 *     <div class="pc-callout" data-hint="2">…
 *     <div class="pc-callout" data-hint="3"><p class="pc-callout-title">Solution</p>…
 *
 * Without this, all three are simply on the page and the reader has read the
 * solution before deciding to. This closes them, puts one button in front of
 * each, opens them one rung at a time, and reveals the problem's reference
 * program under the third. **Nothing is deducted for opening one**: each click
 * is one recorded event and that is all (docs/35 §5 item 3).
 *
 * Three things it takes care to do:
 *
 *   1. **It never removes the authored markup**, only hides it, so a rung that
 *      opens is the author's own words and not a copy of them.
 *   2. **It is idempotent**, guarded by `data-pc-ladder`, for the same reason
 *      `enhanceCodeBlocks` and `hydrateWidgets` are: React can recreate the
 *      `dangerouslySetInnerHTML` subtree after the effect has run.
 *   3. **A card it cannot read is left exactly as it was.** A problem with no
 *      key in its heading, or with no `data-hint` blocks at all, keeps the page
 *      it always had rather than losing a paragraph to a failed enhancement.
 *
 * The injected chrome is raw DOM, which is the same thing `enhanceCodeBlocks`
 * does for the copy button and for the same reason: sanitized lesson HTML
 * cannot carry a `<button>`, so the button is added after render and styled by
 * `progsity-content.css` (`AGENTS.md` rule 17 is about component code, and this
 * is the one place in the app where chrome is injected into stored HTML).
 */

/** "Problem 7: char-code" gives `char-code`. */
const KEY_IN_HEADING = /problem\s*\d+\s*:\s*([a-z0-9-]+)/i;

export type HintLadderLabels = {
  /** `{n}` is the rung number. */
  hint: string;
  solution: string;
  /** The heading above the revealed reference program. */
  referenceSolution: string;
};

export type HintLadderOptions = {
  labels: HintLadderLabels;
  /** Reference programs by problem key, for the third rung. */
  solutions: Record<string, { languageKey: string; solutionCode: string }>;
  /** Steps already open for this reader, so a reload does not close them. */
  openedSteps: readonly string[];
  /** Called once per rung the reader opens, with `hint:<key>:<rung>`. */
  onOpen: (step: string) => void;
  /**
   * `LN-ACC01`: the Pro problems this reader cannot reach. The server has cut
   * their solution rung already, so no rung of theirs is "the solution" here,
   * and the card gets an empty `[data-pc-problem-lock]` node after its last
   * hint, where the reader mounts the Pro card.
   */
  lockedKeys?: readonly string[];
};

/** The node a locked problem card carries for the reader's Pro card. */
export const PROBLEM_LOCK_SELECTOR = "[data-pc-problem-lock]";

export function hintStep(problemKey: string, rung: number): string {
  return `hint:${problemKey}:${rung}`;
}

function problemKeyOf(card: HTMLElement): string | null {
  const heading = card.querySelector(".pc-exercise-num")?.textContent ?? "";
  return heading.match(KEY_IN_HEADING)?.[1] ?? null;
}

function makeButton(label: string, rung: number): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "pc-hint-button";
  button.dataset.hintButton = String(rung);
  button.textContent = label;
  return button;
}

/** The reference program, under the solution rung and never above it. */
function makeSolutionBlock(
  code: string,
  languageKey: string,
  heading: string,
): HTMLElement {
  const wrapper = document.createElement("div");
  wrapper.className = "pc-hint-solution";

  const title = document.createElement("p");
  title.className = "pc-hint-solution-title";
  title.textContent = heading;

  const pre = document.createElement("pre");
  const codeEl = document.createElement("code");
  codeEl.className = `language-${languageKey}`;
  codeEl.textContent = code;
  pre.append(codeEl);

  wrapper.append(title, pre);
  return wrapper;
}

export function hydrateHintLadders(root: HTMLElement, options: HintLadderOptions): void {
  root.querySelectorAll<HTMLElement>(".pc-exercise").forEach((card) => {
    if (card.dataset.pcLadder) return;

    const rungs = Array.from(card.querySelectorAll<HTMLElement>("[data-hint]")).sort(
      (a, b) => Number(a.dataset.hint ?? 0) - Number(b.dataset.hint ?? 0),
    );
    const problemKey = problemKeyOf(card);
    if (rungs.length === 0 || !problemKey) {
      card.dataset.pcLadder = "none";
      return;
    }
    card.dataset.pcLadder = "ready";
    const locked = options.lockedKeys?.includes(problemKey) ?? false;

    const opened = new Set(options.openedSteps);
    const buttons: HTMLButtonElement[] = [];
    const openers: Array<(record: boolean) => void> = [];

    rungs.forEach((rungEl, index) => {
      const rung = Number(rungEl.dataset.hint ?? index + 1);
      // A locked card's ladder ends at its hints: the server cut the solution
      // rung, so counting rungs would dress hint 2 up as "Solution".
      const isSolution = !locked && rung >= rungs.length;
      const label = isSolution
        ? options.labels.solution
        : options.labels.hint.replace("{n}", String(rung));
      const button = makeButton(label, rung);
      rungEl.before(button);
      buttons.push(button);

      // The reference program is built once and attached to the rung, so
      // opening and closing never rebuilds it and a card with no stored
      // solution simply has one less thing under its prose.
      if (isSolution && !locked) {
        const solution = options.solutions[problemKey];
        if (solution?.solutionCode) {
          rungEl.after(
            makeSolutionBlock(
              solution.solutionCode,
              solution.languageKey || "c",
              options.labels.referenceSolution,
            ),
          );
        }
      }

      const open = (record: boolean) => {
        rungEl.dataset.hintOpen = "true";
        button.hidden = true;
        const solutionBlock = rungEl.nextElementSibling;
        if (solutionBlock instanceof HTMLElement && solutionBlock.classList.contains("pc-hint-solution")) {
          solutionBlock.dataset.hintOpen = "true";
        }
        if (record) options.onOpen(hintStep(problemKey, rung));
        // One rung at a time: the next button appears only once this one is
        // open, which is what "the reader has to click through each step"
        // means (`STYLE_GUIDE.md` §5).
        const nextButton = buttons[index + 1];
        if (nextButton) nextButton.hidden = false;
      };

      rungEl.dataset.hintOpen = "false";
      button.addEventListener("click", () => open(true));
      // Later rungs stay out of the way until the one before them is open.
      button.hidden = index > 0;
      openers.push(open);
    });

    // `LN-ACC01`: the Pro card's place, after the last hint the reader can open.
    if (locked) {
      const slot = document.createElement("div");
      slot.dataset.pcProblemLock = problemKey;
      (rungs[rungs.length - 1] ?? card).after(slot);
    }

    // Restoring runs after every button exists, not inside the loop above: a
    // rung opening reveals the next rung's button, and on the first pass that
    // button has not been created yet. Restoring in the loop left a reader who
    // reloaded with hint 1 open and no way to reach hint 2.
    rungs.forEach((rungEl, index) => {
      const rung = Number(rungEl.dataset.hint ?? index + 1);
      if (opened.has(hintStep(problemKey, rung))) openers[index]?.(false);
    });
  });
}
